import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';

import { Form } from 'antd';
import styled from 'styled-components';

import { getCommunityActionPlans } from 'apis/ActionPlansAPI';
import ChevronRight from 'common/Icons/ChevronRight';
import { createActionPlanFromForm } from 'components/ActionPlans/actionPlanFormSave';
import CreateEditActionPlanModal, { ActionPlanFormValues } from 'components/ActionPlans/CreateEditActionPlanModal';
import { buildItemTypeOptions } from 'components/ActionPlans/formUtils';
import useActionPlanFocusAreas from 'components/ActionPlans/useActionPlanFocusAreas';
import useActionPlanOwnerOptions from 'components/ActionPlans/useActionPlanOwnerOptions';
import { isFormValidationError } from 'components/ActionPlans/utils';
import GradientBorderButton from 'components/lib/GradientBorderButton';
import LoaderRibbed from 'components/lib/LoaderRibbed';
import { ACTION_PLAN_SOURCE } from 'constants/actionPlan';
import { useAppSelector } from 'store/hook';

const CATEGORY_ACTION_PLANS_QUERY_KEY_SCOPE = 'ai-ci-category-action-plans';
const CATEGORY_ACTION_PLANS_PAGE = 1;
const CATEGORY_ACTION_PLANS_PAGE_SIZE = 100;
const AI_GENERATED_ACTION_PLANS_TITLE = 'AI-Generated Action Plans';
const AI_GENERATED_ACTION_PLAN_LABEL = 'AI-generated Action Plan';
const CREATE_ACTION_PLAN_LABEL = 'Create Action Plan';
const VIEW_ALL_PLANS_LABEL = 'View All Plans';
const EMPTY_ACTION_PLANS_MESSAGE = 'No AI-generated action plans yet.';

type Props = {
  communityId?: number;
  categoryLabel?: string;
  sourceInsightId?: number | null;
};

const Section = styled.div`
  margin-top: 26px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 18px;
`;

const Title = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 20px;
  font-family: var(--font-bold);
  color: var(--text-primary);
`;

const TitleIcon = styled.img`
  width: 28px;
  height: 28px;
  flex-shrink: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 12px;
`;

const PlanList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
`;

const PlanCard = styled.button`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  border: 1px solid var(--lighter-gray);
  border-radius: 6px;
  background: var(--background-gray-light);
  padding: 10px 15px;
  width: fit-content;
  max-width: 100%;
  min-width: 0;
  flex: 0 1 auto;
  min-height: 68px;
  text-align: left;
  cursor: pointer;
`;

const PlanContent = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

const PlanIcon = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  flex-shrink: 0;
  border-radius: 6px;
  background: linear-gradient(
    315deg,
    var(--competitor-purple),
    var(--aici-action-icon-gradient-mid) 50%,
    var(--flamingo-pink) 0%
  );

  img {
    width: 16px;
    height: 16px;
  }
`;

const PlanText = styled.div`
  min-width: 0;
`;

const PlanEyebrow = styled.div`
  font-size: 13px;
  font-family: var(--font-bold);
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PlanName = styled.div`
  margin-top: 2px;
  font-size: 14px;
  color: var(--dark-blue);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const EmptyMessage = styled.div`
  font-size: 12px;
  color: var(--text-secondary);
  padding: 12px 0;
`;

const LoaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 18px 0;
`;

const getSectionTitle = (categoryLabel?: string) =>
  categoryLabel ? `${AI_GENERATED_ACTION_PLANS_TITLE} for ${categoryLabel}` : AI_GENERATED_ACTION_PLANS_TITLE;

const CategoryActionPlans = ({ communityId, categoryLabel, sourceInsightId }: Props) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { selectedCommunity } = useAppSelector((state) => state.global);
  const [createOpen, setCreateOpen] = useState(false);
  const [form] = Form.useForm<ActionPlanFormValues>();
  const { focusAreaOptions } = useActionPlanFocusAreas();

  const { data, isLoading } = useQuery({
    queryKey: [CATEGORY_ACTION_PLANS_QUERY_KEY_SCOPE, communityId, sourceInsightId],
    queryFn: () =>
      getCommunityActionPlans(communityId ?? -1, {
        page: CATEGORY_ACTION_PLANS_PAGE,
        page_size: CATEGORY_ACTION_PLANS_PAGE_SIZE,
      }),
    enabled: Boolean(communityId && sourceInsightId),
    refetchOnWindowFocus: false,
  });

  const ownerOptions = useActionPlanOwnerOptions(selectedCommunity?.company?.id);
  const itemTypeOptions = useMemo(() => buildItemTypeOptions(), []);
  const plans = useMemo(
    () =>
      (data?.results ?? []).filter(
        (plan) =>
          plan.source === ACTION_PLAN_SOURCE.AI_COMPETITIVE_INTELLIGENCE &&
          sourceInsightId &&
          plan.source_insights.includes(sourceInsightId)
      ),
    [data?.results, sourceInsightId]
  );

  const closeModal = () => {
    setCreateOpen(false);
    form.resetFields();
  };

  const { mutateAsync: createPlan, isLoading: isCreatingPlan } = useMutation({
    mutationFn: (values: ActionPlanFormValues) =>
      createActionPlanFromForm({
        communityId: communityId ?? -1,
        sourceInsights: sourceInsightId ? [sourceInsightId] : undefined,
        values,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries([CATEGORY_ACTION_PLANS_QUERY_KEY_SCOPE, communityId, sourceInsightId]);
      queryClient.invalidateQueries(['action-plans', communityId]);
      queryClient.invalidateQueries(['action-plans-metrics', communityId]);
      queryClient.invalidateQueries(['action-plans-owners', communityId]);
      closeModal();
    },
  });

  const openCreateModal = () => {
    form.resetFields();
    form.setFieldsValue({
      name: categoryLabel ? `${categoryLabel} Action Plan` : '',
      items: [{ action: '' }],
    });
    setCreateOpen(true);
  };

  const submitPlan = async () => {
    if (isCreatingPlan) {
      return;
    }

    try {
      const values = await form.validateFields();
      await createPlan(values);
    } catch (error) {
      if (isFormValidationError(error)) {
        return;
      }

      throw error;
    }
  };

  if (!sourceInsightId) {
    return null;
  }

  return (
    <Section data-test-id="ai-ci-category-action-plans">
      <Header>
        <Title>
          <TitleIcon src="/icons/ai-blob.svg" alt="" />
          {getSectionTitle(categoryLabel)}
        </Title>
        <HeaderActions>
          <GradientBorderButton
            size="small"
            disabled={!communityId}
            onClick={openCreateModal}
            data-test-id="ai-ci-category-action-plans-create-button"
          >
            {CREATE_ACTION_PLAN_LABEL} <ChevronRight fill="var(--competitor-purple)" width={16} height={16} />
          </GradientBorderButton>
          <GradientBorderButton
            size="small"
            disabled={!communityId}
            onClick={() => navigate(`/community/${communityId}/action-plans`)}
            data-test-id="ai-ci-category-action-plans-view-all-button"
          >
            {VIEW_ALL_PLANS_LABEL} <ChevronRight fill="var(--competitor-purple)" width={16} height={16} />
          </GradientBorderButton>
        </HeaderActions>
      </Header>

      {isLoading && (
        <LoaderContainer>
          <LoaderRibbed active />
        </LoaderContainer>
      )}

      {!isLoading && plans.length === 0 && <EmptyMessage>{EMPTY_ACTION_PLANS_MESSAGE}</EmptyMessage>}

      {!isLoading && plans.length > 0 && (
        <PlanList>
          {plans.map((plan) => (
            <PlanCard
              key={plan.id}
              type="button"
              onClick={() => navigate(`/community/${communityId}/action-plans/${plan.id}`)}
            >
              <PlanContent>
                <PlanIcon>
                  <img src="/icons/checklist-white.svg" alt="" />
                </PlanIcon>
                <PlanText>
                  <PlanEyebrow>{AI_GENERATED_ACTION_PLAN_LABEL}</PlanEyebrow>
                  <PlanName>{plan.name}</PlanName>
                </PlanText>
              </PlanContent>
              <ChevronRight fill="var(--text-primary)" width={24} height={24} />
            </PlanCard>
          ))}
        </PlanList>
      )}

      <CreateEditActionPlanModal
        categoryOptions={focusAreaOptions}
        itemTypeOptions={itemTypeOptions}
        open={createOpen}
        isEditing={false}
        isSaving={isCreatingPlan}
        form={form}
        ownerOptions={ownerOptions}
        onCancel={closeModal}
        onSubmit={submitPlan}
      />
    </Section>
  );
};

export default CategoryActionPlans;
